import { countriesList, UUID } from '@cmg/common';
import { Grid, LoadingButton, SnackbarManager } from '@cmg/design-system';
import { xcSelectors } from '@cmg/e2e-selectors';
import React, { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ServerErrorsBanner from '../../../common/components/indicators/server-error/ServerErrorsBanner';
import Spinner from '../../../common/components/overlays/spinner/Spinner';
import { FormikUnsavedChangesGuardContextProvider } from '../../../common/context/formik-unsaved-changes';
import routeFactory from '../../../common/util/routeFactory';
import { CreateOfferingDraftInput, OfferingType, UpdateBasicInfoInput } from '../../../graphql';
import { useOfferingSetupIsReadOnly } from '../../offering-setup-next/hooks/useOfferingSetupIsReadOnly';
import OfferingSetupPage from '../components/design-system/page/OfferingSetupPage';
import { useOfferingSetup_BasicInfoRoute_CreateOfferingDraftMutation } from '../graphql';
import { getOfferingPricingCountryCode } from '../utils/offeringTypeUtil';
import { useValidateOffering } from '../validation/hooks/useValidateOffering';
import BasicInfoForm from './BasicInfoForm';
import { FormType } from './BasicInfoForm.model';
import { useBasicInfoQuery } from './hooks/useBasicInfoQuery';
import { useUpdateBasicInfoMutation } from './hooks/useUpdateBasicInfoMutation';

export type BasicInfoRouteParams = { offeringId?: UUID; stepId?: string };

export type ExtenedCreateOfferingInput = Omit<CreateOfferingDraftInput, 'type'> & {
  type: OfferingType;
};

/**
 * Basic Info Route for Offering Setup
 * Creates an offering draft or updates info for an existing offering
 */
const BasicInfoRoute: React.FC = () => {
  const { offeringId } = useParams<BasicInfoRouteParams>();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlCountryCode = searchParams.get('countryCode') ?? countriesList.Usa.countryCode;
  const pricingCurrencyCodeParam = searchParams.get('pricingCurrencyCode') ?? 'USD';
  const { isReadOnly } = useOfferingSetupIsReadOnly(offeringId);

  const { data, loading, error, refetch } = useBasicInfoQuery({ offeringId });

  const effectiveCountryCode =
    getOfferingPricingCountryCode({
      instruments: data.offering?.instruments,
      pricingInstrumentId: data.offering?.pricingInstrumentId,
    }) ?? urlCountryCode;
  const { revalidate } = useValidateOffering(offeringId);

  const [createOfferingDraft, { loading: creating, error: createError, called: created }] =
    useOfferingSetup_BasicInfoRoute_CreateOfferingDraftMutation();

  const [updateBasicInfo, { loading: updating, error: updateError }] = useUpdateBasicInfoMutation();

  const handleUpdateBasicInfo = useCallback(
    async (values: UpdateBasicInfoInput, id: UUID) => {
      try {
        await updateBasicInfo({
          variables: { input: { ...values, offeringId: id } },
        });

        SnackbarManager.success('Updated Offering Info');
        // re-run validation if necessary to resolve errors/warnings
        revalidate();
        await refetch();
      } catch {
        SnackbarManager.error('Failed to Update Offering Info');
        throw new Error('Failed to Update Offering Info');
      }
    },
    [refetch, revalidate, updateBasicInfo]
  );

  const handleCreateOfferingDraft = useCallback(
    async (values: ExtenedCreateOfferingInput) => {
      const { type } = values;

      try {
        const { data: createOfferingDraftData } = await createOfferingDraft({
          variables: {
            input: {
              ...values,
              type,
              countryCode: urlCountryCode,
            },
          },
        });

        SnackbarManager.success('Successfully Created Offering Draft');

        const draftOfferingId = createOfferingDraftData?.createOfferingDraft?.offering?.id;

        if (draftOfferingId) {
          const stepId = type === OfferingType.PrivatePlacement ? 'private-terms' : 'pricing';
          const path = routeFactory.offeringSetup.getUrlPath({
            offeringId: draftOfferingId,
            stepId,
          });

          // pass unmapped offering type so side nav knows how to rearrange steps
          navigate(path, { state: { offeringType: values.type } });
        }
      } catch {
        SnackbarManager.error('Failed to Create Offering Draft');
      }
    },
    [urlCountryCode, createOfferingDraft, navigate]
  );

  const handleSubmit = useCallback(
    async (values: FormType) => {
      if (offeringId) {
        // remove pricingCurrencyCode and isInternal from DTO - only used for creating offering
        const { pricingCurrencyCode, isInternal, ...updateValues } =
          values as CreateOfferingDraftInput;
        await handleUpdateBasicInfo(updateValues as UpdateBasicInfoInput, offeringId);
      } else {
        // if offeringId doesn't exist, we should be creating a draft
        await handleCreateOfferingDraft(values as ExtenedCreateOfferingInput);
      }
    },
    [handleCreateOfferingDraft, handleUpdateBasicInfo, offeringId]
  );

  const errors = error || createError || updateError;
  const getPanelHeaderContent = useCallback(() => {
    if (data.offering && !isReadOnly) {
      return (
        <LoadingButton loading={updating} variant="contained" type="submit" key="save">
          Save
        </LoadingButton>
      );
    }

    if (!data.offering) {
      return (
        <LoadingButton loading={creating} variant="contained" type="submit" key="create">
          Create Draft
        </LoadingButton>
      );
    }

    return undefined;
  }, [creating, data.offering, isReadOnly, updating]);

  if (loading) {
    return <Spinner show />;
  }

  return (
    <OfferingSetupPage>
      <FormikUnsavedChangesGuardContextProvider>
        <Grid px={4} py={2} data-testid={xcSelectors.offeringSetupBasicInfoScreen.testId}>
          {errors && <ServerErrorsBanner error={errors} />}
          <BasicInfoForm
            offering={data.offering}
            publishedOffering={data.publishedOffering}
            panelHeaderContent={getPanelHeaderContent()}
            disableUnsavedChangesGuard={created && !createError}
            submitting={updating || creating}
            onSubmit={handleSubmit}
            countryCode={effectiveCountryCode}
            initialPricingCurrencyCode={pricingCurrencyCodeParam}
          />
        </Grid>
      </FormikUnsavedChangesGuardContextProvider>
    </OfferingSetupPage>
  );
};

export default BasicInfoRoute;
